'use client';

/**
 * useSectionViewTracking
 *
 * 섹션이 뷰포트에 진입할 때 트래킹하는 훅
 * Intersection Observer 사용
 *
 * @example
 * ```tsx
 * function PromotionPage() {
 *   const heroRef = useSectionViewTracking('Reach Promotion Section', {
 *     properties: { section: 'hero' }
 *   });
 *
 *   return <section ref={heroRef}>Hero Section</section>;
 * }
 * ```
 */
import { useEffect, useRef } from 'react';
import { Analytics } from '../tracker';

interface UseSectionViewTrackingOptions {
  /** 이벤트와 함께 전송할 추가 속성 */
  properties?: Record<string, unknown>;
  /** 트래킹 활성화 여부 (기본값: true) */
  enabled?: boolean;
  /** 뷰포트 진입 threshold (0-1, 기본값: 0.5 = 50% 보일 때) */
  threshold?: number;
  /** 한 번만 트래킹할지 여부 (기본값: true) */
  once?: boolean;
}

/**
 * 섹션 뷰포트 진입 트래킹 훅
 *
 * @param eventName - 발생시킬 이벤트 이름
 * @param options - 옵션
 * @returns ref를 섹션 요소에 연결
 */
export function useSectionViewTracking<T extends HTMLElement = HTMLDivElement>(
  eventName: string,
  options: UseSectionViewTrackingOptions = {},
): React.RefObject<T | null> {
  const { properties = {}, enabled = true, threshold = 0.5, once = true } = options;

  const elementRef = useRef<T>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const element = elementRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (once && hasTrackedRef.current) {
              return;
            }

            Analytics.track(eventName, properties);
            hasTrackedRef.current = true;

            if (once) {
              observer.disconnect();
            }
          }
        });
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [eventName, properties, enabled, threshold, once]);

  return elementRef;
}
